/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC, PropsWithChildren } from 'react'
import React, { useMemo } from 'react'

import type { GridProps } from './types'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import type { Theme } from '../../../theme-provider/types'
import { BoxPrivate } from '../../_primitives/box/box'

const px = (n: number | string) => (typeof n === 'number' ? n + 'px' : n)

const widthToColumns = (width: any): any =>
  Array.isArray(width)
    ? width.map(widthToColumns)
    : width
      ? `repeat(auto-fit, minmax(${px(width)}, 1fr))`
      : null

const countToColumns = (n: any, minColumnWidth: string | number): any =>
  Array.isArray(n)
    ? n.map(i => countToColumns(i, minColumnWidth))
    : n
      ? typeof n === 'number'
        ? `repeat(${n}, minmax(${minColumnWidth}, 1fr))`
        : n
      : null

type GridPrivateProps = PropsWithChildren<
  GridProps & PrivateThemeUiProps & { theme?: Theme }
>

export const GridPrivate = React.forwardRef<HTMLDivElement, GridPrivateProps>(
  function GridPrivate(props, ref) {
    const {
      __css,
      __themeKey,
      className,
      width,
      columns,
      minColumnWidth = 0,
      gridTemplateColumns,
      ...rest
    } = props

    const baseStyle = useMemo(() => {
      const _gridTemplateColumns = gridTemplateColumns
        ? gridTemplateColumns
        : width
          ? widthToColumns(width)
          : columns
            ? countToColumns(columns, minColumnWidth)
            : undefined
      return {
        display: 'grid',
        gridTemplateColumns: _gridTemplateColumns,
        ...(gridTemplateColumns && width ? { width } : {}),
        ...__css
      }
    }, [__css, columns, gridTemplateColumns, width, minColumnWidth])

    return (
      <BoxPrivate
        ref={ref}
        className={classNames(
          {
            'csg-tui-grid': true
          },
          className
        )}
        {...rest}
        __themeKey={__themeKey ? __themeKey : 'grids'}
        __css={baseStyle}
      />
    )
  }
)

const Grid: FC<GridProps> = React.forwardRef<HTMLDivElement, GridProps>(
  function Grid(props, ref) {
    return <GridPrivate ref={ref} {...props} />
  }
)

export default Grid
